import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import {
  User,
  Calendar,
  Activity,
  Heart,
  Target,
  Stethoscope,
  FileText,
} from "lucide-react";
import { MemberSidebar } from "@/components/layout/MemberSidebar";
import { MemberHeader } from "@/components/layout/MemberHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { ROUTES } from "@/routes";

import { PatientDemographics } from "@/components/patient/PatientDemographics";
import { PatientMedicalInfo } from "@/components/patient/PatientMedicalInfo";
import { PatientAppointments } from "@/components/patient/PatientAppointments";
import { PatientCarePlans } from "@/components/patient/PatientCarePlans";
import { PatientIntakeForm } from "@/components/patient/PatientIntakeForm";

export default function MemberProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const memberId = user?.id;

  const { data: patient, isLoading: patientLoading } = useQuery({
    queryKey: ["member-profile", memberId],
    queryFn: async () => {
      if (!memberId) return null;
      const { data, error } = await supabase
        .from("patients")
        .select("*")
        .eq("user_id", memberId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!memberId,
  });

  const { data: vitals } = useQuery({
    queryKey: ["member-vitals", memberId],
    queryFn: async () => {
      if (!memberId) return null;
      const { data, error } = await supabase
        .from("patient_vitals")
        .select("*")
        .eq("member_id", memberId)
        .order("recorded_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!memberId,
  });

  const { data: appointments } = useQuery({
    queryKey: ["member-profile-appointments", memberId],
    queryFn: async () => {
      if (!memberId) return [];
      const { data, error } = await supabase
        .from("telehealth_sessions")
        .select("*")
        .eq("member_id", memberId)
        .order("scheduled_date", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data || [];
    },
    enabled: !!memberId,
  });

  const { data: carePlans } = useQuery({
    queryKey: ["member-profile-care-plans", memberId],
    queryFn: async () => {
      if (!memberId) return [];
      const { data, error } = await supabase
        .from("care_plans")
        .select(`*, care_plan_goals (*)`)
        .eq("member_id", memberId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!memberId,
  });

  if (patientLoading) {
    return (
      <div className="min-h-screen bg-background">
        <MemberSidebar />
        <div className="ml-64">
          <MemberHeader />
          <main className="p-6">
            <div className="space-y-6">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-96 w-full" />
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!patient) {
    // Log missing profile to Sentry
    Sentry.captureMessage(`User ${memberId} is missing the Profile Record`, {
      level: "error",
      extra: { userId: memberId },
    });

    const handleCreateProfile = () => {
      Sentry.addBreadcrumb({
        category: "profile",
        message: `User ${memberId} created a profile record`,
        level: "info",
      });
      navigate(ROUTES.MEMBER_ONBOARDING);
    };

    return (
      <div className="min-h-screen bg-background">
        <MemberSidebar />
        <div className="ml-64">
          <MemberHeader />
          <main className="p-6">
            <div className="text-center py-12">
              <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Profile Not Found</h2>
              <p className="text-muted-foreground mb-6">
                Please complete your intake form to set up your profile.
              </p>
              <Button onClick={handleCreateProfile}>
                Create My Profile
              </Button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const patientName = `${patient.first_name} ${patient.last_name}`;
  const initials = `${patient.first_name?.[0] || ""}${patient.last_name?.[0] || ""}`.toUpperCase();
  const age = patient.date_of_birth
    ? Math.floor((new Date().getTime() - new Date(patient.date_of_birth).getTime()) / 31557600000)
    : null;

  return (
    <div className="min-h-screen bg-background">
      <MemberSidebar />
      <div className="ml-64">
        <MemberHeader />
        <main className="p-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Avatar className="h-16 w-16">
              <AvatarImage src={patient.patient_photo_url || undefined} />
              <AvatarFallback className="text-lg bg-primary/10 text-primary">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h1 className="text-2xl font-bold">{patientName}</h1>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {age && <span>{age} years old</span>}
                <span>•</span>
                <Badge variant="outline" className="capitalize">
                  {patient.insurance_type?.replace("_", " ")}
                </Badge>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate(ROUTES.MEMBER_INTAKE, {
              state: { editData: patient, patientId: patient.id }
            })}>
              Modify My Data
            </Button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <Heart className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Blood Pressure</p>
                    <p className="font-semibold">
                      {vitals?.systolic_bp && vitals?.diastolic_bp
                        ? `${vitals.systolic_bp}/${vitals.diastolic_bp}`
                        : "—"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Activity className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Heart Rate</p>
                    <p className="font-semibold">
                      {vitals?.heart_rate ? `${vitals.heart_rate} bpm` : "—"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Target className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Active Goals</p>
                    <p className="font-semibold">
                      {carePlans?.reduce((acc, cp) => acc + (cp.care_plan_goals?.length || 0), 0) || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Sessions</p>
                    <p className="font-semibold">{appointments?.length || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="demographics" className="space-y-4">
            <TabsList className="w-full overflow-x-auto flex-wrap h-auto gap-1">
              <TabsTrigger value="demographics">
                <User className="h-4 w-4 mr-2" />
                Demographics
              </TabsTrigger>
              <TabsTrigger value="medical">
                <Stethoscope className="h-4 w-4 mr-2" />
                Medical
              </TabsTrigger>
              <TabsTrigger value="appointments">
                <Calendar className="h-4 w-4 mr-2" />
                Appointments
              </TabsTrigger>
              <TabsTrigger value="care-plans">
                <Target className="h-4 w-4 mr-2" />
                Care Plans
              </TabsTrigger>
              <TabsTrigger value="intake-form">
                <FileText className="h-4 w-4 mr-2" />
                Intake Form
              </TabsTrigger>
            </TabsList>

            <TabsContent value="demographics">
              <PatientDemographics patient={patient} patientId={memberId} />
            </TabsContent>
            <TabsContent value="medical">
              <PatientMedicalInfo patient={patient} vitals={vitals} />
            </TabsContent>
            <TabsContent value="appointments">
              <PatientAppointments appointments={appointments || []} patientId={memberId!} />
            </TabsContent>
            <TabsContent value="care-plans">
              <PatientCarePlans carePlans={carePlans || []} patientId={memberId!} />
            </TabsContent>
            <TabsContent value="intake-form">
              <PatientIntakeForm patient={patient} />
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}
